import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from './AuthContext';
import { useToast } from '../../components/Toast';

/**
 * Troca de senha OBRIGATÓRIA no 1º acesso (2026-07-04).
 *
 * Membro cadastrado manualmente por owner/manager recebe senha
 * temporária (mustChangePassword=true). Este gate bloqueia o app até a
 * troca. Após trocar, deslogamos e mandamos re-logar com a senha nova
 * (token novo já vem com a flag limpa). Backend é a verdade.
 */
export function ForcePasswordChange() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('A nova senha deve ter ao menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirm) {
      setError('A confirmação não confere com a nova senha.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da temporária.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/password/change', { currentPassword, newPassword });
      toast.success('Senha alterada. Entre novamente com a nova senha.');
      logout();
      navigate('/login', { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Não foi possível trocar a senha.';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 px-4">
      <div className="w-full max-w-md rounded-xl border border-ink-200 bg-white shadow-xl">
        <header className="px-6 py-5 border-b border-ink-200">
          <h1 className="text-lg font-semibold text-ink-800">Defina sua senha</h1>
          <p className="mt-1 text-sm text-ink-500">
            Você entrou com uma senha temporária. Crie uma senha pessoal para
            continuar.
          </p>
        </header>
        <form className="px-6 py-5 space-y-4" onSubmit={onSubmit}>
          <div>
            <label htmlFor="fp-current" className={labelCx}>Senha temporária</label>
            <input id="fp-current" type="password" autoComplete="current-password"
              className={fieldCx} value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="fp-new" className={labelCx}>Nova senha</label>
            <input id="fp-new" type="password" autoComplete="new-password"
              placeholder="Mínimo 8 caracteres" className={fieldCx} value={newPassword}
              onChange={(e) => setNew(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="fp-confirm" className={labelCx}>Confirmar nova senha</label>
            <input id="fp-confirm" type="password" autoComplete="new-password"
              className={fieldCx} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          {error && (
            <div role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between pt-1">
            <button type="button"
              onClick={() => { logout(); navigate('/login', { replace: true }); }}
              className="text-sm text-ink-500 hover:text-ink-700">
              Sair
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 rounded-md text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:bg-ink-300">
              {loading ? 'Salvando…' : 'Salvar e continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelCx = 'block text-sm font-medium text-ink-700 mb-1';
const fieldCx =
  'w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300';
